/**
 * Biometric authentication service for Capacitor native apps.
 * Uses @capgo/capacitor-native-biometric to store/retrieve auth tokens
 * from the iOS Keychain (Face ID / Touch ID protected).
 *
 * This service is a no-op when running outside Capacitor.
 *
 * Enrollment tracking: a localStorage flag (BIOMETRIC_ENROLLED_KEY) is used
 * instead of isCredentialsSaved() because the latter triggers a Face ID prompt
 * on iOS when credentials are stored with BIOMETRY_ANY access control.
 */

import { clearBiometricEnrolled } from './use-biometric-token-sync';

const SERVER_NAME = 'zameel-auth';

// AccessControl enum values from @capgo/capacitor-native-biometric
// Inlined to avoid importing the plugin at module level (breaks SSR).
const ACCESS_CONTROL_BIOMETRY_ANY = 2;

interface AvailableResult {
  isAvailable: boolean;
  biometryType: number;
  strongBiometryIsAvailable: boolean;
}

interface Credentials {
  username: string;
  password: string;
}

interface NativeBiometricPlugin {
  isAvailable: (options?: {
    useFallback: boolean;
  }) => Promise<AvailableResult>;
  verifyIdentity: (options: {
    reason: string;
    title?: string;
    subtitle?: string;
    description?: string;
    useFallback?: boolean;
  }) => Promise<void>;
  setCredentials: (options: {
    username: string;
    password: string;
    server: string;
    accessControl?: number;
  }) => Promise<void>;
  getCredentials: (options: { server: string }) => Promise<Credentials>;
  getSecureCredentials: (options: {
    server: string;
    reason?: string;
    title?: string;
  }) => Promise<Credentials>;
  deleteCredentials: (options: { server: string }) => Promise<void>;
  isCredentialsSaved: (options: {
    server: string;
  }) => Promise<{ isSaved: boolean }>;
}

function getPlugin(): NativeBiometricPlugin | null {
  if (typeof window === 'undefined') return null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cap = (window as any).Capacitor;
  if (!cap?.isNativePlatform?.()) return null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const plugins = cap.Plugins as Record<string, any> | undefined;
  return (plugins?.NativeBiometric as NativeBiometricPlugin) ?? null;
}

export async function isBiometricAvailable(): Promise<boolean> {
  const plugin = getPlugin();
  if (!plugin) return false;

  try {
    const result = await plugin.isAvailable({ useFallback: true });
    return result.isAvailable;
  } catch {
    return false;
  }
}

export async function hasBiometricCredentials(): Promise<boolean> {
  const plugin = getPlugin();
  if (!plugin) return false;

  try {
    const result = await plugin.isCredentialsSaved({ server: SERVER_NAME });
    return result.isSaved;
  } catch {
    return false;
  }
}

export async function verifyBiometricIdentity(
  reason = 'Sign in to your account',
): Promise<boolean> {
  const plugin = getPlugin();
  if (!plugin) return false;

  try {
    await plugin.verifyIdentity({ reason, title: 'Sign In', useFallback: true });
    return true;
  } catch {
    return false;
  }
}

/**
 * Store auth credentials in the Keychain (biometric-protected).
 * Uses BIOMETRY_ANY access control so Face ID / Touch ID must pass
 * before the OS will decrypt and return the value.
 */
export async function storeBiometricCredentials(
  userId: string,
  refreshToken: string,
): Promise<boolean> {
  const plugin = getPlugin();
  if (!plugin) return false;

  try {
    await plugin.setCredentials({
      username: userId,
      password: refreshToken,
      server: SERVER_NAME,
      accessControl: ACCESS_CONTROL_BIOMETRY_ANY,
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Retrieve stored auth credentials from the Keychain.
 * Uses getSecureCredentials which combines Face ID + Keychain read in one
 * OS-level operation (required for BIOMETRY_ANY access control).
 */
export async function retrieveBiometricCredentials(): Promise<{
  userId: string;
  refreshToken: string;
} | null> {
  const plugin = getPlugin();
  if (!plugin) return null;

  try {
    const result = await plugin.getSecureCredentials({
      server: SERVER_NAME,
      reason: 'Sign in to your account',
      title: 'Sign In',
    });
    return { userId: result.username, refreshToken: result.password };
  } catch {
    return null;
  }
}

export async function clearBiometricCredentials(): Promise<void> {
  // Clear the enrollment flag first so the token sync hook stops running
  // even if the Keychain delete below fails.
  clearBiometricEnrolled();

  const plugin = getPlugin();
  if (!plugin) return;

  try {
    await plugin.deleteCredentials({ server: SERVER_NAME });
  } catch {
    // Credentials may not exist (e.g. cleared by OS after biometric change).
    // Safe to ignore — the enrollment flag is already cleared above.
  }
}
